// =====================================================================
// PLENARIUM:PROTOCOLO_CORRECOES + PLENARIUM:PROTOCOLO_COMISSOES
// Pecas usadas na tela Protocolos (admin) e no Painel do Operador:
//  - OpcoesComissoes: as comissoes da camara como destino da tramitacao
//  - BotaoEditarProtocolo: corrigir os dados do protocolo
//  - HistoricoTramitacao: o historico com corrigir/apagar cada movimentacao
//    e a marca "corrigido" (com o que mudou, quem e quando)
// =====================================================================
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Pencil, Trash2, X, History } from 'lucide-react';

export const SETORES_PROTOCOLO = ['Presidência', 'Mesa Diretora', 'Secretaria', 'Jurídico', 'Financeiro', 'Plenário', 'Protocolo', 'Arquivo'];
const quando = (d) => (d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

let cacheComissoes = null;
function useComissoes() {
  const [lista, setLista] = useState(cacheComissoes || []);
  useEffect(() => {
    let vivo = true;
    axios.get('/api/protocolos/destinos/comissoes').then(r => { cacheComissoes = Array.isArray(r.data) ? r.data : []; if (vivo) setLista(cacheComissoes); }).catch(() => {});
    return () => { vivo = false; };
  }, []);
  return lista;
}

// <optgroup> com as comissoes, para colocar dentro do <select> de setor
export function OpcoesComissoes() {
  const comissoes = useComissoes();
  if (!comissoes.length) return null;
  return (
    <optgroup label="Comissões (os membros recebem no tablet)">
      {comissoes.map(c => <option key={c.id} value={c.valor}>{c.rotulo}</option>)}
    </optgroup>
  );
}

function Janela({ titulo, onFechar, children }) {
  return (
    <div className="modal-overlay" onClick={onFechar}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title"><Pencil size={17} /> {titulo}</h2>
          <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={onFechar}><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function BotaoEditarProtocolo({ protocolo, onSalvo }) {
  const [aberto, setAberto] = useState(false);
  const [f, setF] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const abrir = () => {
    const ts = (protocolo.tramitacoes || []).slice().sort((a, b) => new Date(a.criadoEm) - new Date(b.criadoEm));
    setF({ tipo: protocolo.tipo || 'ENTRADA', remetente: protocolo.remetente || '', destinatario: protocolo.destinatario || '', assunto: protocolo.assunto || '', descricao: protocolo.descricao || '', recebidoPor: (ts[0] && ts[0].usuarioNome) || '' });
    setAberto(true);
  };
  const salvar = async (e) => {
    e.preventDefault(); setSalvando(true);
    try {
      const r = await axios.put(`/api/protocolos/${protocolo.id}`, f);
      toast.success(r.data && r.data.mudou ? 'Protocolo corrigido. A mudança ficou registrada.' : 'Nada foi alterado.');
      setAberto(false); if (onSalvo) onSalvo();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao salvar'); }
    finally { setSalvando(false); }
  };
  const campo = (k, rot, props = {}) => (
    <div className="form-group">
      <label className="form-label">{rot}</label>
      {props.area
        ? <textarea className="form-input" rows={6} value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })} style={{ resize: 'vertical' }} />
        : <input className="form-input" value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })} required={!props.opcional} />}
    </div>
  );
  return (
    <>
      <button type="button" className="btn btn-secondary btn-sm" onClick={abrir} title="Corrigir os dados do protocolo"><Pencil size={13} /> Editar</button>
      {aberto && f && (
        <Janela titulo={`Corrigir protocolo ${protocolo.numero}`} onFechar={() => !salvando && setAberto(false)}>
          <form onSubmit={salvar}>
            <div className="form-group">
              <label className="form-label">Tipo</label>
              <select className="form-input form-select" value={f.tipo} onChange={e => setF({ ...f, tipo: e.target.value })}>
                <option value="ENTRADA">Entrada</option>
                <option value="SAIDA">Saída</option>
              </select>
            </div>
            {campo('remetente', 'Remetente *')}
            {campo('destinatario', 'Destinatário *')}
            {campo('assunto', 'Assunto *')}
            {campo('descricao', 'Descrição', { area: true, opcional: true })}
            {campo('recebidoPor', 'Recebido por *')}
            <p style={{ fontSize: '.78rem', color: '#64748b', margin: '0 0 12px' }}>A correção fica registrada com o valor antigo, o novo, quem mudou e quando. O número e a data do protocolo não mudam.</p>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" disabled={salvando} onClick={() => setAberto(false)}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar correção'}</button>
            </div>
          </form>
        </Janela>
      )}
    </>
  );
}

function EditarMovimentacao({ protocolo, t, onFechar, onSalvo }) {
  const comissoes = useComissoes();
  const atual = (comissoes.find(c => c.rotulo === t.setor) || {}).valor || t.setor;
  const [f, setF] = useState({ setor: atual, observacao: t.observacao || '' });
  const [salvando, setSalvando] = useState(false);
  useEffect(() => { setF(x => (x.setor === t.setor ? { ...x, setor: atual } : x)); }, [atual, t.setor]);
  const opcoes = SETORES_PROTOCOLO.includes(t.setor) || comissoes.some(c => c.rotulo === t.setor) ? SETORES_PROTOCOLO : [t.setor, ...SETORES_PROTOCOLO];
  const salvar = async (e) => {
    e.preventDefault(); setSalvando(true);
    try {
      const r = await axios.put(`/api/protocolos/${protocolo.id}/tramitacoes/${t.id}`, f);
      toast.success(r.data && r.data.mudou ? 'Movimentação corrigida.' : 'Nada foi alterado.');
      onSalvo();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao salvar'); }
    finally { setSalvando(false); }
  };
  return (
    <Janela titulo={`Corrigir movimentação de ${quando(t.criadoEm)}`} onFechar={() => !salvando && onFechar()}>
      <form onSubmit={salvar}>
        <div className="form-group">
          <label className="form-label">Setor *</label>
          <select className="form-input form-select" value={f.setor} onChange={e => setF({ ...f, setor: e.target.value })} required>
            {opcoes.map(s => <option key={s} value={s}>{s}</option>)}
            <OpcoesComissoes />
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Observação</label>
          <textarea className="form-input" rows={4} value={f.observacao} onChange={e => setF({ ...f, observacao: e.target.value })} style={{ resize: 'vertical' }} />
        </div>
        <p style={{ fontSize: '.78rem', color: '#64748b', margin: '0 0 12px' }}>A data e quem lançou a movimentação não mudam. A correção fica registrada.</p>
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" disabled={salvando} onClick={onFechar}>Cancelar</button>
          <button type="submit" className="btn btn-primary" disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar correção'}</button>
        </div>
      </form>
    </Janela>
  );
}

export function HistoricoTramitacao({ protocolo, onMudou }) {
  const [correcoes, setCorrecoes] = useState([]);
  const [verCorr, setVerCorr] = useState(false);
  const [editando, setEditando] = useState(null);
  const lerCorrecoes = useCallback(() => {
    axios.get(`/api/protocolos/${protocolo.id}/correcoes`).then(r => setCorrecoes(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [protocolo.id]);
  useEffect(() => { lerCorrecoes(); }, [lerCorrecoes]);
  const lista = (protocolo.tramitacoes || []).slice().sort((a, b) => new Date(a.criadoEm) - new Date(b.criadoEm));
  const corrigida = (tid) => correcoes.filter(c => c.tramitacaoId === tid);
  const mudou = () => { setEditando(null); lerCorrecoes(); if (onMudou) onMudou(); };
  const apagar = async (t, i) => {
    if (!window.confirm(`Apagar a movimentação ${i + 1} (${t.setor}, ${quando(t.criadoEm)})?\n\nUse só para movimentação lançada por engano. Fica registrado quem apagou e o que era.`)) return;
    try { await axios.delete(`/api/protocolos/${protocolo.id}/tramitacoes/${t.id}`); toast.success('Movimentação apagada (registrado).'); mudou(); }
    catch (err) { toast.error(err.response?.data?.error || 'Erro ao apagar'); }
  };
  const bt = { background: 'none', border: '1px solid var(--cinza, #e2e8f0)', borderRadius: 6, padding: '2px 6px', cursor: 'pointer', color: 'var(--texto-suave, #64748b)', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '.72rem' };
  return (
    <div style={{ marginTop: 12, paddingLeft: 16, borderLeft: '3px solid var(--primaria)' }}>
      <p style={{ fontSize: '0.78rem', fontWeight: 700, marginBottom: 8, color: 'var(--texto-suave)' }}>HISTÓRICO DE TRAMITAÇÃO</p>
      {lista.map((t, i) => {
        const cs = corrigida(t.id);
        return (
          <div key={t.id} style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'flex-start' }}>
            <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--primaria)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', flexShrink: 0, marginTop: 2 }}>{i + 1}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: '0.82rem', fontWeight: 600 }}>{t.setor} — <span style={{ fontWeight: 400 }}>{t.usuarioNome}</span>
                {cs.length > 0 && <span title={cs.map(c => `${c.campo}: "${c.antes || ''}" → "${c.depois || ''}" (${c.por || ''}, ${quando(c.criadoEm)})`).join('\n')} style={{ marginLeft: 6, fontSize: '.68rem', fontWeight: 800, color: '#92400e', background: '#fef3c7', borderRadius: 999, padding: '1px 7px' }}>CORRIGIDO</span>}
              </p>
              {t.observacao && <p style={{ fontSize: '0.78rem', color: 'var(--texto-suave)', whiteSpace: 'pre-wrap' }}>{t.observacao}</p>}
              <p style={{ fontSize: '0.72rem', color: 'var(--texto-suave)' }}>{quando(t.criadoEm)}</p>
            </div>
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              <button type="button" style={bt} onClick={() => setEditando(t)} title="Corrigir esta movimentação"><Pencil size={12} /> Corrigir</button>
              {i > 0 && <button type="button" style={bt} onClick={() => apagar(t, i)} title="Apagar movimentação lançada por engano"><Trash2 size={12} /></button>}
            </div>
          </div>
        );
      })}
      {correcoes.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <button type="button" style={bt} onClick={() => setVerCorr(!verCorr)}><History size={12} /> {verCorr ? 'Esconder' : 'Ver'} correções ({correcoes.length})</button>
          {verCorr && (
            <div style={{ marginTop: 6, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px' }}>
              {correcoes.map(c => (
                <p key={c.id} style={{ fontSize: '.75rem', margin: '2px 0', color: '#78350f' }}>
                  <b>{quando(c.criadoEm)}</b> · {c.por || '—'} · <b>{c.campo}</b>: “{c.antes || '—'}”{c.depois != null ? <> → “{c.depois}”</> : null}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
      {editando && <EditarMovimentacao protocolo={protocolo} t={editando} onFechar={() => setEditando(null)} onSalvo={mudou} />}
    </div>
  );
}
